import { parseActiveFacet } from "../../../helpers/GlobalHelpers";
import { FILTER_CONSTANTS } from "../searchConstants";

const { FILTERS_URL_PARAMETER_NAME, EXTENDED_FILTERS_URL_PARAMETER_NAME } = FILTER_CONSTANTS;

/**
 * Custom hook for managing search query parameters and URL state
 */
export const useSearchQuery = () => {
  const urlParams = new URLSearchParams(window.location.search);

  /**
   * Get facets as query parameter
   */
  const getFacetsQueryParam = (activeFacets) => {
    return encodeURIComponent(activeFacets.map((facet) => facet.id).join(","));
  };

  /**
   * Get refinements as query parameter
   */
  const getRefinementsQueryParam = (activeRefinement) => {
    return encodeURIComponent(activeRefinement);
  };

  /**
   * Get filters from URL query parameter
   */
  const getFiltersQueryParam = () => {
    return encodeURIComponent(urlParams.get(FILTERS_URL_PARAMETER_NAME));
  };

  /**
   * Parse filter objects from query parameter
   */
  const getFilterObjectsFromQueryParam = () => {
    const filtersParam = urlParams.get(FILTERS_URL_PARAMETER_NAME);

    if (!filtersParam) return [];

    const filtersArray = filtersParam.split(",");
    let filters = [];
    filtersArray.map((filter) => {
      const { facetId } = parseActiveFacet(filter);
      filters.push({
        id: filter,
        name: facetId,
      });
      return filter;
    });

    return filters;
  };

  /**
   * Build complete filter query string
   */
  const buildFiltersQueryString = (activeFacets, searchTerm) => {
    let filterQueryString = "";

    if (activeFacets.length > 0) {
      filterQueryString = filterQueryString + "&filters=" + getFacetsQueryParam(activeFacets);
    }

    //if we came to the search page from a "view more" cta on the pdp compatible products component, a "filters" query string was passed in to use in the servet call.
    // add additional filter from page's query string
    if (urlParams.get("filters") && !searchTerm) {
      filterQueryString =
        filterQueryString +
        (filterQueryString.includes("filters")
          ? `,${getFiltersQueryParam()}`
          : "&filters=" + getFiltersQueryParam());
    }

    filterQueryString = filterQueryString + "&url=" + window.location.href;

    let currentCookieString = document.cookie;
    if (currentCookieString) {
      let cookieArray = currentCookieString.split("; ");
      for (let i = 0; i < cookieArray.length; i++) {
        let cookie = cookieArray[i];
        let index = cookie.indexOf("=");
        if (index > 0) {
          let cookieName = cookie.substring(0, index);
          try {
            cookieName = decodeURIComponent(cookieName);
          } catch (e) {
            // do nothing;
          }
          if (cookieName === "_br_uid_2") {
            let cookieValue = decodeURIComponent(cookie.substring(index + 1));
            filterQueryString = filterQueryString + "&brUid2=" + cookieValue;
          }
        }
      }
    }

    return filterQueryString;
  };

  /**
   * Remove filter from page URL
   */
  const removeFilterFromPageURL = () => {
    urlParams.delete(FILTERS_URL_PARAMETER_NAME);
    urlParams.delete(EXTENDED_FILTERS_URL_PARAMETER_NAME);
    const queryString = urlParams.toString().length > 0 ? "?" + urlParams.toString() : "";

    window.history.pushState(null, "", window.location.pathname + queryString);
  };

  /**
   * Remove URL filter and trigger search
   */
  const removeUrlFilter = (setLoadingResults, setPageNum, setReRunSearch, reRunSearch) => {
    setLoadingResults(true);
    removeFilterFromPageURL();

    setPageNum(1);
    setReRunSearch(!reRunSearch);
  };

  /**
   * Build refinement servlet URL
   */
  const buildRefinementServlet = (
    patentSearchServletURL,
    brsmSearchURL,
    configurationPath,
    searchTerm,
    activeFacets
  ) => {
    const filterString = buildFiltersQueryString(activeFacets, searchTerm);
    const searchQuery = searchTerm ? "&q=" + encodeURIComponent(searchTerm) : "";

    if (patentSearchServletURL) {
      return `${patentSearchServletURL}?componentConfigPath=${configurationPath}${searchQuery}${filterString}`;
    } else {
      return `${brsmSearchURL}?componentConfigPath=${configurationPath}${searchQuery}${filterString}`;
    }
  };

  /**
   * Update browser URL with current search state
   */
  const updateSearchURL = (searchTerm, activeRefinement, sortValue, pageNum, activeFacets) => {
    const params = new URLSearchParams(window.location.search);
    
    // Update search term
    if (searchTerm) {
      params.set('q', searchTerm);
    } else {
      params.delete('q');
    }
    
    // Update refinement type
    if (activeRefinement) {
      params.set('type', activeRefinement);
    } else {
      params.delete('type');
    }
    
    // Update sort
    if (sortValue && sortValue !== 'featured') {
      params.set('sort', sortValue);
    } else {
      params.delete('sort');
    }
    
    // Update page number
    if (pageNum && pageNum > 1) {
      params.set('page', pageNum);
    } else {
      params.delete('page');
    }
    
    // Update facet filters
    if (activeFacets && activeFacets.length > 0) {
      params.set('filters', activeFacets.map(f => f.id).join(','));
    } else {
      params.delete('filters');
    }
    
    const queryString = params.toString();
    const newURL = window.location.pathname + (queryString ? '?' + queryString : '');
    
    // Use replaceState to avoid creating browser history entries for every keystroke
    window.history.replaceState(null, '', newURL);
  };

  return {
    urlParams,
    getFacetsQueryParam,
    getRefinementsQueryParam,
    getFiltersQueryParam,
    getFilterObjectsFromQueryParam,
    buildFiltersQueryString,
    removeFilterFromPageURL,
    removeUrlFilter,
    buildRefinementServlet,
    updateSearchURL,
  };
};
